import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DashboardHeader from './DashboardHeader';

// Charts
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Colors,
  Legend,
  LinearScale,
  Title,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import HtmlLegendPlugin from 'views/Charts/HtmlLegendPlugin';
ChartJS.register(
  BarElement,
  ArcElement,
  LinearScale,
  Title,
  CategoryScale,
  Colors,
  Legend
);
const pieColors = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
];

// @mui/material components
import Skeleton from '@mui/material/Skeleton';
import { makeStyles } from '@mui/styles';

// @mui/icons-material
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Broken from '@mui/icons-material/BrokenImage';
import Workstation from '@mui/icons-material/Computer';
import DateRange from '@mui/icons-material/DateRange';
import Equalizer from '@mui/icons-material/Equalizer';
import FunctionsIcon from '@mui/icons-material/Functions';
import User from '@mui/icons-material/Person';
import PieChartIcon from '@mui/icons-material/PieChartOutline';
import Print from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsRemote from '@mui/icons-material/SettingsRemote';
import Store from '@mui/icons-material/Store';
import VerticalSplit from '@mui/icons-material/VerticalSplit';

// core components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import styles from 'assets/jss/material-dashboard-pro-react/views/dashboardStyle.js';

import {
  setTenantLogsCriteria,
} from '../../features/tenant/tenantSlice';

import { useHistory } from 'react-router-dom';
import { selectSessionStatus } from '../../features/session/sessionSlice';
import { useLazyGetTenantStatsQuery } from 'api/tenant.api';
import dashboardDates from './dashboardDates.const';

const useStyles = makeStyles(styles);

export default function TenantDashboard() {
  const [triggerGetStats, {data: tenantStats, isFetching, isSuccess}] = useLazyGetTenantStatsQuery();
  const sessionStatus = useSelector(selectSessionStatus);
  const statsStatus = isFetching ? 'pending' : isSuccess ? 'success' : 'failure';
  const dispatch = useDispatch();
  const history = useHistory();

  var locationCount = tenantStats ? tenantStats.locationCount : 0;
  var workstationCount = tenantStats ? tenantStats.workstationCount : 0;
  var userCount = tenantStats ? tenantStats.userCount : 0;
  var transactionCount = tenantStats ? tenantStats.transactionCount : 0;
  var printerCount = tenantStats ? tenantStats.printerCount : 0;
  var pedCount = tenantStats ? tenantStats.pedCount : 0;
  var cashdrawerCount = tenantStats ? tenantStats.cashdrawerCount : 0;
  var scannerCount = tenantStats ? tenantStats.scannerCount : 0;
  var logErrorCount = tenantStats ? tenantStats.logErrorCount : 0;
  var orphanedSessionCount = tenantStats ? tenantStats.orphanedSessionCount : 0;
  var unreconciledPaymentCount = tenantStats
    ? tenantStats.unreconciledPaymentCount
    : 0;

  const [initialized, setInitialized] = useState(false);
  const [dateRange, setDateRange] = React.useState(dashboardDates[0]);

  useEffect(() => {
    if (!initialized && sessionStatus === 'active') setInitialized(true);
  }, [initialized, sessionStatus]);

  useEffect(() => {
    if (sessionStatus === 'active') setRefresh(true);
  }, [dateRange]);

  const [refresh, setRefresh] = useState(false);
  const [refreshAvailable, setRefreshAvailable] = useState(false);

  const refreshStats = () => {
    if (initialized && sessionStatus === 'active') {
      setRefresh(true);
    }
  };

  let timer = useRef();

  useEffect(() => {
    if (refresh) {
      const period = dateRange.replace(/\s+/g, '').toLowerCase();
      triggerGetStats({period}).unwrap();
      setRefresh(false);
      setRefreshAvailable(false);
      timer.current = setTimeout(() => {
        setRefreshAvailable(true);
      }, 10000);
    }
  }, [refresh]);

  useEffect(() => {
    return () => clearTimeout(timer.current);
  }, []);

  const [paymentTypePieChart, setPaymentTypePieChart] = useState({
    labels: [],
    datasets: [],
  });

  const [refundTypePieChart, setRefundTypePieChart] = useState({
    labels: [],
    datasets: [],
  });

  const [activityTypePieChart, setActivityTypePieChart] = useState({
    labels: [],
    datasets: [],
  });

  const [locationActivityBarChart, setLocationActivityBarChart] = useState({
    labels: [],
    datasets: [],
  });

  var paymentTypePieChartRef = useRef(null);
  var refundTypePieChartRef = useRef(null);
  var activityTypePieChartRef = useRef(null);
  var locationActivityBarChartRef = useRef(null);

  const populatePaymentStats = () => {
    let labels = [];
    let series = [];
    let seriesSum = 0;

    const sortedSummary = tenantStats.paymentTypeSummary.toSorted((a, b) =>
      a._id.localeCompare(b._id)
    );
    sortedSummary.forEach((tender) => {
      series.push(tender.count);
      seriesSum += tender.count;
    });
    sortedSummary.forEach((tender) => {
      labels.push(
        tender._id + '\n' + Math.round((tender.count / seriesSum) * 100) + '%'
      );
    });

    setPaymentTypePieChart({
      ...(paymentTypePieChart || {}),
      labels: labels,
      datasets: [
        {
          data: series,
          borderWidth: 1,
          backgroundColor: [...pieColors],
        },
      ],
    });
  };

  const populateRefundStats = () => {
    let labels = [];
    let series = [];
    let seriesSum = 0;

    const sortedSummary = tenantStats.refundTypeSummary.toSorted((a, b) =>
      a._id.localeCompare(b._id)
    );
    sortedSummary.forEach((tender) => {
      series.push(tender.count);
      seriesSum += tender.count;
    });
    sortedSummary.forEach((tender) => {
      labels.push(
        tender._id + '\n' + Math.round((tender.count / seriesSum) * 100) + '%'
      );
    });

    setRefundTypePieChart({
      ...(refundTypePieChart || {}),
      labels: labels,
      datasets: [
        {
          data: series,
          borderWidth: 1,
          backgroundColor: [...pieColors],
        },
      ],
    });
  };

  const populateActivityStats = () => {
    let labels = [];
    let series = [];
    let seriesSum = 0;

    const sortedSummary = tenantStats.sessionTypeSummary.toSorted((a, b) =>
      a._id.localeCompare(b._id)
    );
    sortedSummary.forEach((session) => {
      series.push(session.count);
      seriesSum += session.count;
    });
    sortedSummary.forEach((session) => {
      let label = '';
      switch (session._id) {
        case 'payment':
          label = 'Payments';
          break;
        case 'refund':
          label = 'Refunds';
          break;
        case 'cashmanagement':
          label = 'Cash Management';
          break;
        case 'devicemanagement':
          label = 'Device Management';
          break;
        default:
          label = session._id;
      }
      labels.push(
        label + '\n' + Math.round((session.count / seriesSum) * 100) + '%'
      );
    });

    setActivityTypePieChart({
      ...(activityTypePieChart || {}),
      labels: labels,
      datasets: [
        {
          data: series,
          borderWidth: 1,
          backgroundColor: [...pieColors],
        },
      ],
    });
  };

  const populateLocationStats = () => {
    let labels = [];
    let series = [];
    tenantStats.sessionLocationSummary.forEach((location) => {
      labels.push(location._id);
      series.push(location.count);
    });

    setLocationActivityBarChart({
      ...(locationActivityBarChart || {}),
      labels: labels,
      datasets: [
        {
          data: series,
          borderWidth: 1,
        },
      ],
    });
  };

  useEffect(() => {
    if (initialized && statsStatus === 'success') {
      if (tenantStats.paymentTypeSummary.length > 0) populatePaymentStats();
      if (tenantStats.refundTypeSummary.length > 0) populateRefundStats();
      if (tenantStats.sessionTypeSummary.length > 0) populateActivityStats();
      if (tenantStats.sessionLocationSummary.length > 0)
        populateLocationStats();
    }
  }, [tenantStats, initialized, statsStatus]);

  const classes = useStyles();

  const getPeriodDates = (period) => {
    const now = new Date();

    let start = null;
    let end = null;
    switch (period) {
      case 'today':
        start = now.setHours(0, 0, 0, 0);
        end = now.setHours(23, 59, 59, 999);
        break;
      case 'thisweek':
        start = new Date(
          new Date().setDate(now.getDate() - now.getDay())
        ).setHours(0, 0, 0, 0);
        end = new Date(
          new Date().setDate(now.getDate() - now.getDay() + 6)
        ).setHours(23, 59, 59, 999);
        break;
      case 'thismonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1).setHours(
          0,
          0,
          0,
          0
        );
        end = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        ).setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        start = new Date(new Date().setDate(now.getDate() - 1)).setHours(
          0,
          0,
          0,
          0
        );
        end = new Date(new Date().setDate(now.getDate() - 1)).setHours(
          23,
          59,
          59,
          999
        );
        break;
      case 'lastweek':
        start = new Date(
          new Date().setDate(now.getDate() - now.getDay() - 7)
        ).setHours(0, 0, 0, 0);
        end = new Date(
          new Date().setDate(now.getDate() - now.getDay())
        ).setHours(23, 59, 59, 999);
        break;
      case 'lastmonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1).setHours(
          0,
          0,
          0,
          0
        );
        end = new Date(
          now.getFullYear(),
          now.getMonth(),
          0,
          23,
          59,
          59,
          999
        ).setHours(23, 59, 59, 999);
        break;
      default:
        start = now.setHours(0, 0, 0, 0);
        end = now.setHours(23, 59, 59, 999);
        break;
    }
    return {
      start: new Date(start),
      end: new Date(end),
    };
  };

  const goToLogs = async () => {
    let period = getPeriodDates(dateRange.replace(/\s+/g, '').toLowerCase());
    await dispatch(
      setTenantLogsCriteria({
        applicationTypes: ['client'],
        levelTypes: ['error'],
        messageTypes: ['all'],
        startDate: period.start,
        endDate: period.end,
      })
    );
    history.push('/admin/logs');
  };

  return (
    <div>
      <GridContainer>
        <GridItem xs={10}>
          <DashboardHeader
            dates={dashboardDates}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </GridItem>
        <GridItem xs={2}>
          <Button
            color='info'
            onClick={refreshStats}
            className={classes.refreshButton}
            startIcon={<RefreshIcon />}
            disabled={!refreshAvailable}
          >
            Refresh
          </Button>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='info' stats icon>
              <CardIcon color='info'>
                <Store />
              </CardIcon>
              <p className={classes.cardCategory}>Locations</p>
              <h3 className={classes.cardTitle}>{locationCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='info' stats icon>
              <CardIcon color='info'>
                <Workstation />
              </CardIcon>
              <p className={classes.cardCategory}>Workstations</p>
              <h3 className={classes.cardTitle}>{workstationCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='info' stats icon>
              <CardIcon color='info'>
                <User />
              </CardIcon>
              <p className={classes.cardCategory}>Users</p>
              <h3 className={classes.cardTitle}>{userCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='info' stats icon>
              <CardIcon color='info'>
                <ReceiptIcon />
              </CardIcon>
              <p className={classes.cardCategory}>Transactions</p>
              <h3 className={classes.cardTitle}>{transactionCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                {dateRange}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='success' stats icon>
              <CardIcon color='success'>
                <Print />
              </CardIcon>
              <p className={classes.cardCategory}>Printers</p>
              <h3 className={classes.cardTitle}>{printerCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='success' stats icon>
              <CardIcon color='success'>
                <VerticalSplit />
              </CardIcon>
              <p className={classes.cardCategory}>Cash Drawers</p>
              <h3 className={classes.cardTitle}>{cashdrawerCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='success' stats icon>
              <CardIcon color='success'>
                <AccountBalanceWallet />
              </CardIcon>
              <p className={classes.cardCategory}>PEDs</p>
              <h3 className={classes.cardTitle}>{pedCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardHeader color='success' stats icon>
              <CardIcon color='success'>
                <SettingsRemote />
              </CardIcon>
              <p className={classes.cardCategory}>Scanners</p>
              <h3 className={classes.cardTitle}>{scannerCount}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                <FunctionsIcon />
                Active
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <Card>
            <CardHeader color='danger' icon>
              <CardIcon color='danger'>
                <PieChartIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Activity Types</h4>
            </CardHeader>
            <CardBody>
              {tenantStats && tenantStats.sessionTypeSummary.length > 0 ? (
                <div style={{ width: '100%' }}>
                  <div className={classes.pieChartPieContainer}>
                    <Pie
                      data={activityTypePieChart}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          htmlLegend: {
                            // ID of the container to put the legend in
                            containerID: 'legends-activity-pie-chart',
                          },
                          legend: {
                            display: false,
                          },
                        },
                      }}
                      plugins={[HtmlLegendPlugin]}
                    />
                  </div>
                  <div
                    id='legends-activity-pie-chart'
                    className={classes.pieChartHtmlLegendContainer}
                  ></div>
                </div>
              ) : (
                <Skeleton
                  variant='circular'
                  width={205}
                  height={205}
                  sx={{ margin: 'auto', marginTop: 5, marginBottom: 5 }}
                />
              )}
            </CardBody>
            <CardFooter stats chart>
              <div className={classes.stats}>
                <DateRange />
                {tenantStats && tenantStats.sessionTypeSummary.length > 0
                  ? dateRange
                  : 'No Available Data'}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <Card>
            <CardHeader color='danger' icon>
              <CardIcon color='danger'>
                <PieChartIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Payment Types</h4>
            </CardHeader>
            <CardBody>
              {tenantStats && tenantStats.paymentTypeSummary.length > 0 ? (
                <div style={{ width: '100%' }}>
                  <div className={classes.pieChartPieContainer}>
                    <Pie
                      data={paymentTypePieChart}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          htmlLegend: {
                            // ID of the container to put the legend in
                            containerID: 'legends-paymenttype-pie-chart',
                          },
                          legend: {
                            display: false,
                          },
                        },
                      }}
                      plugins={[HtmlLegendPlugin]}
                    />
                  </div>
                  <div
                    id='legends-paymenttype-pie-chart'
                    className={classes.pieChartHtmlLegendContainer}
                  ></div>
                </div>
              ) : (
                <Skeleton
                  variant='circular'
                  width={205}
                  height={205}
                  sx={{ margin: 'auto', marginTop: 5, marginBottom: 5 }}
                />
              )}
            </CardBody>
            <CardFooter stats chart>
              <div className={classes.stats}>
                <DateRange />
                {tenantStats && tenantStats.paymentTypeSummary.length > 0
                  ? dateRange
                  : 'No Available Data'}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <Card>
            <CardHeader color='danger' icon>
              <CardIcon color='danger'>
                <PieChartIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Refund Types</h4>
            </CardHeader>
            <CardBody>
              {tenantStats && tenantStats.refundTypeSummary.length > 0 ? (
                <div style={{ width: '100%' }}>
                  <div className={classes.pieChartPieContainer}>
                    <Pie
                      data={refundTypePieChart}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          htmlLegend: {
                            // ID of the container to put the legend in
                            containerID: 'legends-refundtype-pie-chart',
                          },
                          legend: {
                            display: false,
                          },
                        },
                      }}
                      plugins={[HtmlLegendPlugin]}
                    />
                  </div>
                  <div
                    id='legends-refundtype-pie-chart'
                    className={classes.pieChartHtmlLegendContainer}
                  ></div>
                </div>
              ) : (
                <Skeleton
                  variant='circular'
                  width={205}
                  height={205}
                  sx={{ margin: 'auto', marginTop: 5, marginBottom: 5 }}
                />
              )}
            </CardBody>
            <CardFooter stats chart>
              <div className={classes.stats}>
                <DateRange />
                {tenantStats && tenantStats.refundTypeSummary.length > 0
                  ? dateRange
                  : 'No Available Data'}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <Card chart>
            <CardHeader color='info' icon>
              <CardIcon color='info'>
                <Equalizer />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Activity by Location</h4>
            </CardHeader>
            <CardBody>
              {tenantStats && tenantStats.sessionLocationSummary.length > 0 ? (
                <div style={{ height: '400px' }}>
                  <Bar
                    data={locationActivityBarChart}
                    options={{
                      indexAxis: 'y',
                      backgroundColor: '#00bcd4',
                      maintainAspectRatio: false,
                      interaction: {
                        intersect: false,
                        axis: 'y',
                        mode: 'index',
                      },
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </>
              )}
            </CardBody>
            <CardFooter stats>
              <div className={classes.stats}>
                <DateRange />
                {tenantStats && tenantStats.sessionLocationSummary.length > 0
                  ? dateRange
                  : 'No Available Data'}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
